/**
 * Operation Description Generator
 * Extracted from useNodeOperations.ts to eliminate duplication
 * Generates human-readable descriptions for all operation types
 */

import type { NodeOperation } from '@/shared/types/projectData';

/**
 * Generate a human-readable description for any node operation
 * Used for undo/redo labels and operation logging
 */
export function generateOperationDescription(operation: NodeOperation): string {
  switch (operation.type) {
    case 'create':
      return `Create ${operation.data?.type || 'item'}`;

    case 'update': {
      const updateFields = Object.keys(operation.data || {}).join(', ') || 'properties';
      return `Update ${updateFields}`;
    }

    case 'delete':
      return 'Delete item';
    case 'delete-tree':
      return 'Delete group and children';
    case 'restore-tree':
      return 'Restore group and children';

    case 'move':
      return 'Move item';

    case 'batch-update': {
      const updateCount = operation.updates ? operation.updates.length : 0;
      return `Update ${updateCount} items`;
    }

    case 'batch-delete': {
      const deleteCount = operation.nodeIds ? operation.nodeIds.length : 0;
      return `Delete ${deleteCount} items`;
    }

    case 'reorder':
      return 'Reorder items';

    case 'copy-nodes': {
      const copyCount = operation.nodeIds ? operation.nodeIds.length : 1;
      return `Copy ${copyCount} item${copyCount > 1 ? 's' : ''}`;
    }

    case 'cut-nodes': {
      const cutCount = operation.nodeIds ? operation.nodeIds.length : 1;
      return `Cut ${cutCount} item${cutCount > 1 ? 's' : ''}`;
    }

    case 'paste-nodes': {
      const pasteCount = operation.clipboardData ? operation.clipboardData.length : 0;
      return `Paste ${pasteCount} item${pasteCount > 1 ? 's' : ''}`;
    }

    case 'duplicate-node':
      return 'Duplicate item';

    case 'duplicate-subtree':
      return 'Duplicate item with children';

    case 'attach-file':
      return 'Attach file';

    case 'detach-file':
      return 'Detach file';

    case 'delete-file':
      return 'Delete file';

    case 'rename-file':
      return `Rename file to ${operation.newFileName || 'new name'}`;

    case 'add-column':
      return `Add column: ${operation.columnData?.title || 'New Column'}`;

    case 'delete-column':
      return 'Delete column';

    case 'update-column':
      return `Update column: ${operation.columnData?.title || 'column'}`;

    case 'reorder-columns':
      return 'Reorder columns';

    case 'group-move':
      return 'Move group';

    default:
      return `${operation.type} operation`;
  }
}
